// File: src/components/admin/UserDetail/UserOverviewTab.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)
// User Overview tab with basic user information using Read-Only by Default pattern

import React, { useState, useCallback } from 'react';
import {
    Typography, TextField, Grid, Divider, Chip, Box, Button, ButtonGroup,
    Alert, CircularProgress, Paper
} from '@mui/material';
import {
    CheckCircle, Cancel, TimerOff, Replay, Add, Remove
} from '@mui/icons-material';
import { useDetailViewContext } from '../../../contexts/DetailViewContext';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';

const UserOverviewTab = ({
    uid,
    user,
    isDisabled
}) => {
    // Get edit state and form data from context
    const { isEditing, formData, validationErrors, setFieldValue } = useDetailViewContext();

    // Trial management state
    const [trialLoading, setTrialLoading] = useState(false);
    const [trialResult, setTrialResult] = useState(null);
    const [trialError, setTrialError] = useState(null);
    const [customDays, setCustomDays] = useState(7);

    const handleTrialAction = useCallback(async (action, days) => {
        setTrialLoading(true);
        setTrialError(null);
        setTrialResult(null);
        try {
            const result = await adminApi.updateUserTrial(uid, { action, days });
            setTrialResult(result);
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setTrialError(errorInfo.message);
        } finally {
            setTrialLoading(false);
        }
    }, [uid]);

    // Handle field change using context's setFieldValue
    const handleChange = (field) => (event) => {
        setFieldValue(field, event.target.value);
    };

    // Fields are disabled if not in edit mode OR if user is archived (disabled)
    const fieldsDisabled = !isEditing || isDisabled;

    return (
        <Grid container spacing={3} data-testid="user-overview-tab">
            <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                    User Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="User ID"
                    value={uid}
                    disabled
                    helperText="User ID cannot be changed"
                    data-testid="user-field-uid"
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Email"
                    value={formData.email || ''}
                    disabled
                    helperText="Email cannot be changed"
                    data-testid="user-field-email"
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Display Name"
                    value={formData.displayName || ''}
                    onChange={handleChange('displayName')}
                    disabled={fieldsDisabled}
                    error={Boolean(validationErrors.displayName)}
                    helperText={validationErrors.displayName}
                    data-testid="user-field-displayName"
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Language"
                    value={formData.language || 'en'}
                    onChange={handleChange('language')}
                    select
                    SelectProps={{ native: true }}
                    disabled={fieldsDisabled}
                    error={Boolean(validationErrors.language)}
                    helperText={validationErrors.language}
                    data-testid="user-field-language"
                >
                    <option value="en">English</option>
                    <option value="de">German</option>
                    <option value="fr">French</option>
                </TextField>
            </Grid>

            {/* Registration & Terms Status */}
            <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Registration & Terms Status
                </Typography>
                <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="user-field-registered">
                    <Typography variant="body2" color="text.secondary">Registered:</Typography>
                    {user?.registeredAt ? (
                        <Chip icon={<CheckCircle />} label="Yes" color="success" size="small" variant="outlined" />
                    ) : (
                        <Chip icon={<Cancel />} label="No" color="default" size="small" variant="outlined" />
                    )}
                </Box>
                {user?.registeredAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Registered: {new Date(user.registeredAt).toLocaleString()}
                    </Typography>
                )}
            </Grid>

            <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="user-field-terms-accepted">
                    <Typography variant="body2" color="text.secondary">Terms Accepted:</Typography>
                    {String(user?.termsAccepted) === 'true' ? (
                        <Chip icon={<CheckCircle />} label="Yes" color="success" size="small" variant="outlined" />
                    ) : (
                        <Chip icon={<Cancel />} label="No" color="error" size="small" variant="outlined" />
                    )}
                </Box>
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Terms Version"
                    value={user?.termsVersion || 'N/A'}
                    disabled
                    helperText="Version of terms the user accepted"
                    data-testid="user-field-termsVersion"
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Terms Accepted At"
                    value={user?.termsAcceptedAt ? new Date(user.termsAcceptedAt).toLocaleString() : 'N/A'}
                    disabled
                    helperText="When the user last accepted terms"
                    data-testid="user-field-termsAcceptedAt"
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="user-field-onboarding-step">
                    <Typography variant="body2" color="text.secondary">Onboarding Step:</Typography>
                    <Chip
                        label={user?.onboardingStep || 'not started'}
                        color={user?.onboardingStep === 'done' ? 'success' : user?.onboardingStep ? 'warning' : 'default'}
                        size="small"
                        variant="outlined"
                    />
                </Box>
            </Grid>

            {/* Trial Period Management */}
            <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Trial Period Management
                </Typography>
                <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }} data-testid="trial-management-section">
                    {/* Current trial info from user data */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Account Created: {user?.createdAt ? new Date(user.createdAt).toLocaleString() : 'N/A'}
                        </Typography>
                    </Box>

                    {/* Trial result display */}
                    {trialResult && (
                        <Alert
                            severity={trialResult.trialActive ? 'success' : 'warning'}
                            sx={{ mb: 2 }}
                            data-testid="trial-result"
                            onClose={() => setTrialResult(null)}
                        >
                            <Typography variant="body2" fontWeight="medium">
                                Action "{trialResult.action}" applied successfully
                            </Typography>
                            <Typography variant="body2">
                                Trial {trialResult.trialActive ? 'Active' : 'Expired'}
                                {trialResult.trialActive
                                    ? ` — ${trialResult.daysRemaining} day(s) remaining`
                                    : ` — expired ${trialResult.daysSinceExpiry} day(s) ago`
                                }
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Expires: {new Date(trialResult.trialExpiresAt).toLocaleString()}
                                {' · '}Duration: {trialResult.trialDurationDays} days
                            </Typography>
                        </Alert>
                    )}

                    {trialError && (
                        <Alert severity="error" sx={{ mb: 2 }} data-testid="trial-error" onClose={() => setTrialError(null)}>
                            {trialError}
                        </Alert>
                    )}

                    {/* Quick actions */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            startIcon={trialLoading ? <CircularProgress size={16} /> : <Add />}
                            onClick={() => handleTrialAction('extend', 7)}
                            disabled={trialLoading || isDisabled}
                            data-testid="trial-extend-7"
                        >
                            +7 Days
                        </Button>
                        <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            startIcon={trialLoading ? <CircularProgress size={16} /> : <Add />}
                            onClick={() => handleTrialAction('extend', 14)}
                            disabled={trialLoading || isDisabled}
                            data-testid="trial-extend-14"
                        >
                            +14 Days
                        </Button>
                        <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            startIcon={trialLoading ? <CircularProgress size={16} /> : <Remove />}
                            onClick={() => handleTrialAction('reduce', 7)}
                            disabled={trialLoading || isDisabled}
                            data-testid="trial-reduce-7"
                        >
                            -7 Days
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={trialLoading ? <CircularProgress size={16} /> : <TimerOff />}
                            onClick={() => handleTrialAction('expire')}
                            disabled={trialLoading || isDisabled}
                            data-testid="trial-expire"
                        >
                            Expire Now
                        </Button>
                        <Button
                            variant="outlined"
                            color="info"
                            size="small"
                            startIcon={trialLoading ? <CircularProgress size={16} /> : <Replay />}
                            onClick={() => handleTrialAction('reset')}
                            disabled={trialLoading || isDisabled}
                            data-testid="trial-reset"
                        >
                            Reset Trial
                        </Button>
                    </Box>

                    {/* Custom days input */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            size="small"
                            type="number"
                            label="Custom days"
                            value={customDays}
                            onChange={(e) => setCustomDays(Math.max(1, parseInt(e.target.value) || 1))}
                            sx={{ width: 120 }}
                            inputProps={{ min: 1, max: 365 }}
                            disabled={trialLoading || isDisabled}
                            data-testid="trial-custom-days"
                        />
                        <ButtonGroup size="small" disabled={trialLoading || isDisabled}>
                            <Button
                                color="success"
                                onClick={() => handleTrialAction('extend', customDays)}
                                data-testid="trial-custom-extend"
                            >
                                Extend
                            </Button>
                            <Button
                                color="warning"
                                onClick={() => handleTrialAction('reduce', customDays)}
                                data-testid="trial-custom-reduce"
                            >
                                Reduce
                            </Button>
                        </ButtonGroup>
                    </Box>
                </Paper>
            </Grid>


            {/* Note about edit mode */}
            {!isEditing && !isDisabled && (
                <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                        Click "Edit User" above to modify user details.
                    </Typography>
                </Grid>
            )}

            {isDisabled && (
                <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                        Restore user to enable editing.
                    </Typography>
                </Grid>
            )}
        </Grid>
    );
};

export default UserOverviewTab;
